import { clsx } from 'clsx'
import { type FC, type ReactNode, useEffect } from 'react'
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl'
import { useIntersectionObserver } from 'usehooks-ts'

import {
  OfferConditionTypeEnumV2,
  OfferDiscountTypeEnumV2,
  type SellerPromotionV2,
} from '@redteclab/api/clients/bully'
import { BrandIconPresent } from '@redteclab/brand-icons'

import { BrandIcon, Link } from 'base-ui'

import {
  EXPERIMENTS_TRACK_EVENT_PDP_SELLER_PROMOTION_BANNER_IMPRESSION,
  experimentsTrackEvent,
} from '../../../experiments/eventTracking'
import { useGlobalConfigContext } from '../../../global-config'
import { urlResolverGetLinkToAllProductsOfSeller } from '../../../url-handling'
import { productAccessorGetSeller } from '../../model/productAccessor/productAccessorGetSeller'
import { productAccessorGetSellerPromotionFromOffer } from '../../model/productAccessor/productAccessorGetSellerPromotionFromOffer'
import { productAccessorOfferHasSellerPromotion } from '../../model/productAccessor/productAccessorOfferHasSellerPromotion'
import { productAccessorStockStatusIsAvailable } from '../../model/productAccessor/productAccessorStockStatusIsAvailable'

import {
  getFormattedPromotionDiscountPercentage,
  getFormattedPromotionDiscountValueWithCurrency,
} from './productSellerPromotionFormatter'
import {
  type ProductSellerPromotionInfoBannerProps,
  type ProductSellerPromotionInfoBannerSize,
} from './ProductSellerPromotionInfoBannerTypes'
import { renderProductSellerPromotionInfoBannerMessage } from './renderProductSellerPromotionInfoBannerMessage'

const renderBadgeOrImage = (
  sellerPromotion: SellerPromotionV2,
  intl: IntlShape,
  size?: ProductSellerPromotionInfoBannerSize,
): ReactNode => {
  if (sellerPromotion.discountType === OfferDiscountTypeEnumV2.FreeItems) {
    return (
      <BrandIcon
        className="shrink-0"
        data-qa-id="product-seller-promotion-banner-image"
        icon={BrandIconPresent}
        size="s"
      />
    )
  }

  return (
    <div
      className={clsx(
        'flex shrink-0 items-center justify-center rounded-full bg-light-tertiary font-mono font-medium',
        size === 'xs' && 'size-12 text-xs',
        size === 's' && 'size-20 text-xl',
      )}
      data-qa-id="product-seller-promotion-banner-badge"
    >
      {/* amount discounts need to be shown as a negative amount, e.g. "-5€" */}
      {sellerPromotion.discountType === OfferDiscountTypeEnumV2.TotalAmount
        ? getFormattedPromotionDiscountValueWithCurrency(
            sellerPromotion.discountValue * -1,
            intl,
          )
        : null}

      {/* percentage discounts need to be shown as a negative amount, e.g. "-10%" */}
      {sellerPromotion.discountType === OfferDiscountTypeEnumV2.TotalPercent
        ? getFormattedPromotionDiscountPercentage(
            sellerPromotion.discountValue * -1,
            intl,
          )
        : null}
    </div>
  )
}

export const ProductSellerPromotionInfoBanner: FC<
  ProductSellerPromotionInfoBannerProps
> = ({ className, offer, product, size = 's' }) => {
  const config = useGlobalConfigContext()
  const intl = useIntl()
  const { isIntersecting, ref } = useIntersectionObserver({
    threshold: 0.95,
  })

  useEffect(() => {
    if (isIntersecting) {
      experimentsTrackEvent(
        config,
        EXPERIMENTS_TRACK_EVENT_PDP_SELLER_PROMOTION_BANNER_IMPRESSION,
      )
    }
  }, [isIntersecting, config])

  if (!offer?.seller?.sellerId) {
    return null
  }

  const productOfferHasSellerPromotion =
    productAccessorOfferHasSellerPromotion(offer)

  const seller = productAccessorGetSeller(offer)
  const sellerPromotion = productAccessorGetSellerPromotionFromOffer(offer)

  const showSellerSerpLink =
    sellerPromotion?.conditionType ===
      OfferConditionTypeEnumV2.MinimumOrderValue ||
    sellerPromotion?.conditionType === OfferConditionTypeEnumV2.MinimumQuantity

  return (
    productAccessorStockStatusIsAvailable(product) &&
    productOfferHasSellerPromotion &&
    sellerPromotion && (
      <div
        className={clsx(
          'flex rounded-2xl bg-light-brand-medium p-4',
          className,
        )}
        data-qa-id="product-seller-promotion-info-banner"
        ref={ref}
      >
        {renderBadgeOrImage(sellerPromotion, intl, size)}
        <div
          className={clsx(
            'ml-4 flex flex-col text-xs tablet:text-s',
            showSellerSerpLink ? 'justify-between' : 'justify-center',
          )}
        >
          <p data-qa-id="product-seller-promotion-banner-description">
            {renderProductSellerPromotionInfoBannerMessage({
              footnoteNumber: config.superScripts.sellerPromotions,
              intl,
              sellerName: seller?.displayName,
              sellerPromotion,
              tenant: config.tenant,
            })}
          </p>
          {showSellerSerpLink ? (
            <Link
              className="tablet:font-medium"
              data-qa-id="product-seller-promotion-banner-seller-serp-link"
              href={urlResolverGetLinkToAllProductsOfSeller(
                config,
                offer.seller.sellerId,
              )}
            >
              <FormattedMessage id="seller.promotion.product.banner.sellerSerpLink" />
            </Link>
          ) : null}
        </div>
      </div>
    )
  )
}
